import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Divider,
  Stack,
  Paper,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { getAllCakeOrder, updateStatus, exportFilteredOrdersExcel } from "../../service/orderService";
import { getAllBranchLists } from "../../service/branchService";
import { useSnackbar } from "../../App";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Color from "../../components/Color";
import { io } from "socket.io-client";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useOrderSocket } from "../../context/webSocketContext";

const statusOptions = [
  { label: "All Status", value: "", color: "default" },
  { label: "Order Placed", value: "order_placed", color: "warning" },
  { label: "Preparing", value: "preparing", color: "#7c0bcc" },
  { label: "Ready for Delivery", value: "ready_for_delivery", color: "info" },
  { label: "Completed", value: "completed", color: "success" },
  { label: "Cancel Requested", value: "request_for_cancel", color: "warning", hideFromTabs: true },
  { label: "Cancel Rejected", value: "cancel_request_rejected", color: "default", hideFromTabs: true },
  { label: "Cancelled", value: "cancelled", color: "error" },
];

const getStatusColor = (status) =>
  statusOptions.find((s) => s.value === status)?.color || "default";

const renderOrderNumber = (val, isMobile, isUnread) => {
  if (!val) return "Data not found";

  let displayVal = val;
  const parts = displayVal.split("-");
  if (parts.length >= 3) {
    const secondToLast = parts[parts.length - 2];
    if (/^\d{4}$/.test(secondToLast)) {
      parts.splice(parts.length - 2, 1);
      displayVal = parts.join("-");
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "nowrap", gap: "6px" }}>
      {displayVal.length <= 4 ? (
        <span style={{ fontWeight: 800, color: "#000000", fontSize: isMobile ? "0.75rem" : "0.90rem", whiteSpace: "nowrap" }}>
          {displayVal}
        </span>
      ) : (
        <>
          <span style={{ color: "#666666", fontSize: isMobile ? "0.75rem" : "0.90rem", whiteSpace: "nowrap" }}>{displayVal.slice(0, -4)}</span>
          <span style={{ fontWeight: 800, color: "#000000", fontSize: isMobile ? "0.75rem" : "0.90rem", whiteSpace: "nowrap" }}>{displayVal.slice(-4)}</span>
        </>
      )}
      {isUnread && (
        <>
          <style>
            {`
              @keyframes pulseGlow {
                0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(233, 30, 99, 0.7); }
                70% { transform: scale(1); box-shadow: 0 0 0 5px rgba(233, 30, 99, 0); }
                100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(233, 30, 99, 0); }
              }
            `}
          </style>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#e91e63",
              color: "#ffffff",
              fontSize: "10px",
              fontWeight: "bold",
              padding: "2px 6px",
              borderRadius: "10px",
              textTransform: "uppercase",
              animation: "pulseGlow 2s infinite",
              marginLeft: "4px",
              lineHeight: "1",
              letterSpacing: "0.5px"
            }}
          >
            New
          </span>
        </>
      )}
    </div>
  );
};

const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

export default function CakeFactory() {
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [filters, setFilters] = useState({
    status: "",
    dateFilter: "all",
    branch_id: "",
    order_date_from: null,
    order_date_to: null
  });
  const pageSize = 20;
  const [sort] = useState({ field: "id", order: "desc" });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [cardboardCount, setCardboardCount] = useState("");
  const [deliverTo, setDeliverTo] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);


  const [completedItems, setCompletedItems] = useState({});

  const selectedOrderIdRef = useRef('');
  const socket = useOrderSocket();

  const [readOrderIds, setReadOrderIds] = useState(() => {
    const saved = localStorage.getItem("read_orders_cake");
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  const openModal = (order) => {
    setSelectedOrder(order);
    selectedOrderIdRef.current = order.id;

    // Mark as read
    setReadOrderIds(prev => {
      if (prev.has(order.id)) return prev;
      const newSet = new Set(prev);
      newSet.add(order.id);
      localStorage.setItem("read_orders_cake", JSON.stringify(Array.from(newSet)));
      return newSet;
    });


    const saved = localStorage.getItem(`completedItems_cake_${order.id}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCompletedItems(prev => ({ ...prev, [order.id]: new Set(parsed) }));
      } catch {
        setCompletedItems(prev => ({ ...prev, [order.id]: new Set() }));
      }
    } else {
      setCompletedItems(prev => ({ ...prev, [order.id]: new Set() }));
    }

    setModalOpen(true);
  };

  const closeModal = () => {
    if (selectedOrder) {
      const orderId = selectedOrder.id;
      const currentSet = completedItems[orderId] || new Set();


      if (currentSet.size > 0 && currentSet.size < selectedOrder.items.length) {
        localStorage.setItem(`completedItems_cake_${orderId}`, JSON.stringify(Array.from(currentSet)));
      } else {
        localStorage.removeItem(`completedItems_cake_${orderId}`);
      }
    }

    setModalOpen(false);
    setSelectedOrder(null);
    selectedOrderIdRef.current = '';
  };

  const formatApiDate = (date) => {
    if (!date || isNaN(new Date(date).getTime())) return null;
    return format(new Date(date), "yyyy-MM-dd");
  };

  const [branches, setBranches] = useState([]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await getAllBranchLists();
        const list = res?.data?.data || res?.data || [];
        setBranches(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Fetch branches error:", err);
      }
    };
    fetchBranches();
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        page,
        limit: pageSize,
        customer_name: debouncedSearch || undefined,
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.branch_id ? { branch_id: filters.branch_id } : {}),
        order_date_from: formatApiDate(filters.order_date_from),
        order_date_to: formatApiDate(filters.order_date_to),
        sort_field: sort.field,
        sort_order: sort.order,
      };

      const res = await getAllCakeOrder(payload);
      const data = res?.data?.data;

      if (Array.isArray(data?.data)) {
        const normalized = data.data.map((o) => {
          const items = (o.items || []).map((item) => ({
            ...item,
            productName: (item.product && item.product.name) || `Product #${item.product_id || item.productid}`,
          }));

          return {
            ...o,
            items,
            customerName: o.customer?.name || "",
            branchName: o.branch?.name || "",
            order_date: o.order_date?.split("T")[0] || "",
          };
        });

        let finalOrders = normalized;
        if (!filters.status) {
          const statusPriority = {
            order_placed: 0,
            ready_for_delivery: 1,
            completed: 2,
            cancelled: 3,
          };
          finalOrders = [...normalized].sort((a, b) => {
            const prioA = statusPriority[a.status] ?? 99;
            const prioB = statusPriority[b.status] ?? 99;
            return prioA - prioB;
          });
        }

        setOrders(finalOrders);
        setTotal(data.total || 0);
      } else {
        setOrders([]);
        setTotal(0);
        showSnackbar("Unexpected API response format", "error");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load cake orders";
      setError(msg);
      setOrders([]);
      setTotal(0);
      showSnackbar(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filters, sort, showSnackbar]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleExportExcel = async () => {
    try {
      showSnackbar("Preparing export...", "info");
      
      const payload = {
        search: debouncedSearch || undefined,
        status: filters.status || undefined,
        branch_id: filters.branch_id || undefined,
        order_date_from: formatApiDate(filters.order_date_from),
        order_date_to: formatApiDate(filters.order_date_to),
        sort_field: sort.field,
        sort_order: sort.order,
        order_category: "cake",
        export_type: "factory",
      };

      const res = await exportFilteredOrdersExcel(payload);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      
      const today = new Date();
      const formattedDate = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
      link.setAttribute("download", `cake_factory_orders_${formattedDate}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSnackbar("Export completed successfully!", "success");
    } catch (err) {
      console.error("Export error:", err);
      showSnackbar("Failed to export orders!", "error");
    }
  };

  const token = localStorage.getItem('token');

  // WebSocket 
  useEffect(() => {
    if (!socket) return;

    const onConnect = () => {
      console.log('socket connected at cake factory');
    };

    const onUpdate = (data) => {
      const { order_id, order_number, status } = data;

      if (status === "order_placed" || status === "ready_for_delivery") {
        fetchOrders();
      }

      setOrders(prevOrders => {
        const updatedOrders = prevOrders.map(order =>
          order.id === order_id || order.order_number === order_number
            ? { ...order, status }
            : order
        );

        if (selectedOrderIdRef.current === order_id) {
          setSelectedOrder(prev => (prev ? { ...prev, status } : prev));
        }

        const statusLabel =
          statusOptions.find(s => s.value === status)?.label || status;
        const displayId = order_number || order_id;

        const message =
          status === "order_placed"
            ? `New order #${displayId} has been created with status: ${statusLabel}`
            : `Order number ${displayId} → status has been updated to ${statusLabel}`;

        showSnackbar(message, {
          variant: "socket",
          autoHideDuration: 8000,
        });

        return updatedOrders;
      });
    };

    const onDisconnect = () => {
      console.log('disconnected from cake factory');
    };

    socket.on('connect', onConnect);
    socket.on("order_status_update", onUpdate);
    socket.on('disconnect', onDisconnect);

    return () => {
      socket.off('connect', onConnect);
      socket.off("order_status_update", onUpdate);
      socket.off('disconnect', onDisconnect);
    };
  }, [socket, fetchOrders, showSnackbar]);

  const handleStatusFilterChange = (event, newStatus) => {
    if (newStatus !== null) {
      setFilters(prev => ({ ...prev, status: newStatus }));
      setPage(1);
    }
  };

  const handleDateFilterChange = (event) => {
    const value = event.target.value;
    const today = new Date();
    let from = null;
    let to = null;

    if (value === "today") {
      from = today;
      to = today;
    }

    setFilters(prev => ({
      ...prev,
      dateFilter: value,
      order_date_from: from,
      order_date_to: to
    }));
    setPage(1);
  };

  const handleStatusChange = (orderId, newStatus) => {
    setPendingOrderId(orderId);
    setPendingStatus(newStatus);
    setCardboardCount("");
    setDeliverTo("");
    setConfirmOpen(true);
  };

  const cancelStatusUpdate = () => {
    setConfirmOpen(false);
    setPendingOrderId(null);
    setPendingStatus(null);
    showSnackbar("Status change cancelled. Your progress is still saved!", "info");
  };

  const confirmStatusUpdate = async () => {
    if (!pendingOrderId || !pendingStatus) return;

    const pendingOrder = orders.find(o => o.id === pendingOrderId) || (selectedOrder?.id === pendingOrderId ? selectedOrder : null);
    const currentStatus = pendingOrder?.cake_status || pendingOrder?.status;
    const isPreparingToReady = pendingStatus === "ready_for_delivery" && currentStatus !== "ready_for_delivery";

    if (isPreparingToReady) {
      if (!cardboardCount || cardboardCount === "" || !deliverTo || deliverTo.trim() === "") {
        showSnackbar("Cardboard Count and Deliver To are required!", "error");
        return;
      }
    }

    setStatusUpdating(true);

    // Optimistic update
    setOrders(prev => prev.map(o => o.id === pendingOrderId ? {
      ...o,
      status: pendingStatus,
      cake_status: pendingStatus,
      cake_carton_box_count: isPreparingToReady ? parseInt(cardboardCount) || 0 : o.cake_carton_box_count,
      carton_box_count: isPreparingToReady ? parseInt(cardboardCount) || 0 : o.carton_box_count,
      deliver_to: isPreparingToReady ? deliverTo : o.deliver_to
    } : o));
    if (selectedOrder?.id === pendingOrderId) {
      setSelectedOrder(prev => (prev ? {
        ...prev,
        status: pendingStatus,
        cake_status: pendingStatus,
        cake_carton_box_count: isPreparingToReady ? parseInt(cardboardCount) || 0 : prev.cake_carton_box_count,
        carton_box_count: isPreparingToReady ? parseInt(cardboardCount) || 0 : prev.carton_box_count,
        deliver_to: isPreparingToReady ? deliverTo : prev.deliver_to
      } : prev));
    }

    try {
      const extraParams = {
        factory_type: 'cake',
        ...(isPreparingToReady ? {
          cake_carton_box_count: parseInt(cardboardCount) || 0,
          deliver_to: deliverTo
        } : {})
      };

      await updateStatus(pendingOrderId, pendingStatus, extraParams);
      showSnackbar("Status updated successfully", "success");

      if (socket?.connected) {
        socket.emit('order_status_update', {
          order_number: selectedOrder?.order_number,
          order_id: selectedOrder?.id,
          status: pendingStatus,
        });
      }

      // Clear local progress ONLY if status is "ready_for_delivery"
      if (pendingStatus === "ready_for_delivery") {
        localStorage.removeItem(`completedItems_cake_${pendingOrderId}`);
        setCompletedItems(prev => {
          const newState = { ...prev };
          delete newState[pendingOrderId];
          return newState;
        });
      }
      closeModal();
      fetchOrders();
    } catch (err) {
      showSnackbar("Failed to update status", "error");
      fetchOrders();
    } finally {
      setStatusUpdating(false);
      setConfirmOpen(false);
      setPendingOrderId(null);
      setPendingStatus(null);
    }
  };

  const toggleItemComplete = (itemId) => {
    if (!selectedOrder) return;

    setCompletedItems(prev => {
      const orderId = selectedOrder.id;
      const currentSet = prev[orderId] || new Set();
      const newSet = new Set(currentSet);

      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }

      // Auto-trigger only if all items completed and not already in final status
      if (
        newSet.size === selectedOrder.items.length &&
        !["ready_for_delivery", "completed"].includes(selectedOrder.cake_status)
      ) {
        handleStatusChange(selectedOrder.id, "ready_for_delivery");
        showSnackbar("All items completed! Please confirm to mark as Ready for Delivery", "success");
      }

      return { ...prev, [orderId]: newSet };
    });
  };

  const columns = [
    {
      field: "order_number",
      headerName: "Order Number",
      flex: 1.2,
      minWidth: isMobile ? 120 : 150,
      renderCell: (params) => {
        const isUnread = !readOrderIds.has(params.row.id) && params.row.status === "order_placed";
        return renderOrderNumber(params.value, isMobile, isUnread);
      }
    },
    {
      field: "branchName",
      headerName: "Branch",
      flex: 1,
      minWidth: isMobile ? 100 : 130,
      renderCell: (params) => (
        <Chip label={params.value || "Data not found"} size="small" color="secondary" sx={{ borderRadius: 1, fontSize: 11 }} />
      )
    },
    {
      field: "delivery_date",
      headerName: "Delivery Date",
      flex: 1,
      minWidth: isMobile ? 100 : 120,
      renderCell: (params) => {
        const val = params.value;
        if (!val) return "Data not found";
        return format(new Date(val), "dd-MM-yyyy");
      }
    },
    {
      field: "cake_status",
      headerName: "Status",
      flex: 1,
      minWidth: isMobile ? 100 : 130,
      renderCell: (params) => {
        const colorValue = getStatusColor(params.value);
        const isHex = colorValue.startsWith("#");
        return (
          <Chip
            label={statusOptions.find((s) => s.value === params.value)?.label || params.value || "Data not found"}
            color={isHex ? undefined : colorValue}
            size="small"
            sx={{
              fontSize: 12,
              height: 24,
              borderRadius: 1,
              ...(isHex && {
                backgroundColor: colorValue,
                color: "#fff",
                fontWeight: 600,
                boxShadow: `0 2px 6px ${colorValue}55`,
              })
            }}
          />
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      minWidth: isMobile ? 80 : 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          color="primary"
          onClick={() => openModal(params.row)}
          sx={{ fontSize: 12, py: 0.5, px: 2, textTransform: "none", borderRadius: 1.5 }}
        >
          View
        </Button>
      )
    }
  ];

  if (error) {
    return <Container sx={{ py: 3 }}><Typography color="error">{error}</Typography></Container>;
  }

  return (
    <Box sx={{ mb: { xs: 2, sm: 2, md: 4, lg: 4 } }}>
      <Typography fontWeight="bold" color={Color.primary.main} mb={1} fontSize={26}>
        Cake Orders
      </Typography>

      <Box
        mb={3}
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: isMobile ? "stretch" : { sm: "flex-start", md: "flex-start", lg: "space-between" },
          alignItems: isMobile ? "stretch" : "center",
          flexWrap: "wrap",
          gap: isMobile ? 1 : 2,
        }}
      >
        <ToggleButtonGroup
          value={filters.status}
          exclusive
          onChange={handleStatusFilterChange}
          aria-label="status filter"
          sx={{
            display: "flex",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? 0.5 : { sm: "4px", md: "6px", lg: "8px" },
            width: isMobile ? "100%" : "auto",
            "& .MuiToggleButton-root": {
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              px: { xs: 1.5, sm: 1.2, md: 1.8, lg: 3 },
              height: "32px",
              fontSize: { xs: 11, sm: 11, md: 12, lg: 12 },
              fontWeight: 500,
              textTransform: "none",
              color: "#666",
              whiteSpace: "nowrap",
              "&.Mui-selected": {
                backgroundColor: Color.primary.main,
                color: "#fff",
                border: `1px solid ${Color.primary.main}`,
                "&:hover": {
                  backgroundColor: Color.primary.main,
                  color: "#fff",
                },
              },
              "&:hover": { backgroundColor: "#f5f5f5" },
            },
          }}
        >
          {statusOptions.filter(opt => !opt.hideFromTabs).map((opt) => (
            <ToggleButton key={opt.value} value={opt.value}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : "10px",
            width: isMobile ? "100%" : "auto",
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          <FormControl size="small" sx={{ width: isMobile ? "100%" : { sm: 130, md: 150, lg: 170 }, height: "37px" }}>
            <Select
              displayEmpty
              value={filters.branch_id}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, branch_id: e.target.value }));
                setPage(1);
              }}
              sx={{
                fontSize: 13,
                borderRadius: "8px",
                height: '37px',
              }}
            >
              <MenuItem value="" sx={{ fontSize: 13 }}>All Branches</MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id} sx={{ fontSize: 13 }}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: isMobile ? "100%" : { sm: 110, md: 130, lg: 150 }, height: "37px" }}>
            <Select
              value={filters.dateFilter || "all"}
              onChange={handleDateFilterChange}
              sx={{
                fontSize: 13,
                borderRadius: "8px",
                height: '37px',
              }}
            >
              <MenuItem value="all" sx={{ fontSize: 13 }}>All Time</MenuItem>
              <MenuItem value="today" sx={{ fontSize: 13 }}>Today</MenuItem>
              <MenuItem value="custom" sx={{ fontSize: 13 }}>Custom Date</MenuItem>
            </Select>
          </FormControl>

          {filters.dateFilter === "custom" && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={filters.order_date_from}
                onChange={(val) => {
                  setFilters((prev) => ({ ...prev, order_date_from: val }));
                  setPage(1);
                }}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "From Date",
                    sx: {
                      width: isMobile ? "100%" : { sm: 110, md: 130, lg: 150 },
                      '& .MuiOutlinedInput-root': {
                        fontSize: 13,
                        height: '37px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        '& input': {
                          height: '100%',
                          boxSizing: 'border-box',
                          py: 0,
                          textAlign: 'center'
                        }
                      }
                    }
                  }
                }}
              />
              <DatePicker
                value={filters.order_date_to}
                onChange={(val) => {
                  setFilters((prev) => ({ ...prev, order_date_to: val }));
                  setPage(1);
                }}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "To Date",
                    sx: {
                      width: isMobile ? "100%" : { sm: 110, md: 130, lg: 150 },
                      '& .MuiOutlinedInput-root': {
                        fontSize: 13,
                        height: '37px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        '& input': {
                          height: '100%',
                          boxSizing: 'border-box',
                          py: 0,
                          textAlign: 'center'
                        }
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setFilters(prev => ({ ...prev, dateFilter: "all", order_date_from: null, order_date_to: null, status: "", branch_id: "" }));
              setPage(1);
            }}
            sx={{
              height: "37px",
              borderColor: Color.secondary.main,
              color: Color.secondary.main,
              borderRadius: "8px",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              width: isMobile ? "100%" : { sm: 70, md: 80, lg: 90 },
              whiteSpace: "nowrap",
              "&:hover": { borderColor: Color.secondary.dark, bgcolor: "rgba(255, 0, 0, 0.04)" }
            }}
          >
            Clear
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            sx={{
              height: "37px",
              backgroundColor: Color.primary.main,
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              width: isMobile ? "100%" : { sm: 80, md: 95, lg: 110 },
              whiteSpace: "nowrap",
              "&:hover": { backgroundColor: Color.primary.dark }
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: "100%", mt: 2 }}>
        <DataGrid
          rows={orders}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          autoHeight
          rowHeight={isMobile ? 48 : 56}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSorting
          hideFooter
          getRowClassName={(params) => {
            const isUnread = !readOrderIds.has(params.row.id) && params.row.status === "order_placed";
            return isUnread ? "unread-row" : "";
          }}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": { fontSize: isMobile ? "14px" : "16px", fontWeight: 600, pl: 1 },
            "& .MuiDataGrid-cell": { fontSize: isMobile ? "12px" : "14px", pl: 2 },
            "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#fff" },
            "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#f4f4f4" },
            "& .unread-row": {
              backgroundColor: "#ffdce7 !important",
              "&:hover": {
                backgroundColor: "#ffeef4 !important",
              },
            },
            "& .MuiLinearProgress-bar": { backgroundColor: Color.primary.main },
            "& .MuiLinearProgress-colorPrimary": { backgroundColor: Color.secondary.main },
            overflowX: isMobile ? "hidden" : "auto",
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
          }}
        />
      </Box>

      {total > 0 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination count={Math.ceil(total / pageSize)} page={page} onChange={(e, p) => setPage(p)} color="primary" size="large" />
        </Box>
      )}

      {/* Modal */}
      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography fontSize={14} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Order Number: {renderOrderNumber(selectedOrder?.order_number, isMobile)}
            </Typography>
            <IconButton onClick={closeModal}><CloseIcon color="error" /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: "10px",
                border: "1px solid",
                borderColor: "rgba(124, 11, 204, 0.15)",
                background: "linear-gradient(135deg, rgba(124, 11, 204, 0.02) 0%, rgba(124, 11, 204, 0.05) 100%)",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="caption" display="block" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                  Branch
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: Color.primary.main, mt: 0.5 }}>
                  {selectedOrder.branchName || "Data not found"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" display="block" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                  Status
                </Typography>
                 <Box mt={0.5}>
                  {(() => {
                    const colorValue = getStatusColor(selectedOrder.cake_status);
                    const isHex = colorValue.startsWith("#");
                    return (
                      <Chip
                        label={statusOptions.find((s) => s.value === selectedOrder.cake_status)?.label || selectedOrder.cake_status || "Unknown"}
                        color={isHex ? undefined : colorValue}
                        size="small"
                        sx={{
                          fontSize: 12,
                          height: 24,
                          borderRadius: 1,
                          fontWeight: 600,
                          ...(isHex && {
                            backgroundColor: colorValue,
                            color: "#fff",
                            boxShadow: `0 2px 6px ${colorValue}55`,
                          })
                        }}
                      />
                    );
                  })()}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" display="block" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                  Order Date
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", mt: 0.5 }}>
                  {selectedOrder.order_date ? format(new Date(selectedOrder.order_date), "dd-MM-yyyy") : "Data not found"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" display="block" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                  Delivery Date
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", mt: 0.5 }}>
                  {selectedOrder.delivery_date ? format(new Date(selectedOrder.delivery_date), "dd-MM-yyyy") : "Data not found"}
                </Typography>
              </Box>
              {((selectedOrder.cake_carton_box_count !== undefined && selectedOrder.cake_carton_box_count !== null && selectedOrder.cake_carton_box_count !== "") ||
                (selectedOrder.carton_box_count !== undefined && selectedOrder.carton_box_count !== null && selectedOrder.carton_box_count !== "")) && (
                <Box>
                  <Typography variant="caption" display="block" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                    Cake Carton Box Count
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", mt: 0.5 }}>
                    {selectedOrder.cake_carton_box_count !== undefined && selectedOrder.cake_carton_box_count !== null && selectedOrder.cake_carton_box_count !== ""
                      ? selectedOrder.cake_carton_box_count
                      : selectedOrder.carton_box_count}
                  </Typography>
                </Box>
              )}
              {selectedOrder.deliver_to && selectedOrder.deliver_to.trim() !== "" ? (
                <Box>
                  <Typography variant="caption" display="block" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                    Deliver To
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", mt: 0.5 }}>
                    {selectedOrder.deliver_to}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small" disabled={["cancelled", "completed", "cancel_request_rejected", "request_for_cancel"].includes(selectedOrder?.cake_status || selectedOrder?.status)}>
              <InputLabel sx={{ fontSize: 13 }}>Change Status</InputLabel>
              <Select
                value={selectedOrder?.cake_status || selectedOrder?.status || ""}
                label="Change Status"
                onChange={(e) => { e.stopPropagation(); handleStatusChange(selectedOrder?.id, e.target.value); }}
                sx={{ fontSize: 13 }}
              >
                {statusOptions
                  .slice(1)
                  .filter((opt) => {
                    const currentOrdStatus = selectedOrder?.cake_status || selectedOrder?.status;
                    const cancelRequestStatuses = ["cancel_requested", "request_for_cancel"];
                    // Cancelled / Completed: locked final states, visible only in their own state or when cancellation is pending
                    if (["cancelled", "completed"].includes(opt.value)) {
                      return (
                        currentOrdStatus === opt.value ||
                        cancelRequestStatuses.includes(currentOrdStatus)
                      );
                    }
                    // cancel_request_rejected: only visible when status is request_for_cancel or cancel_request_rejected
                    if (opt.value === "cancel_request_rejected") {
                      return (
                        currentOrdStatus === "request_for_cancel" ||
                        currentOrdStatus === "cancel_request_rejected"
                      );
                    }
                    // cancel_requested: only visible in its own state
                    if (opt.value === "cancel_requested") {
                      return currentOrdStatus === "cancel_requested";
                    }
                    // request_for_cancel: only visible in its own state
                    if (opt.value === "request_for_cancel") {
                      return currentOrdStatus === "request_for_cancel";
                    }
                    return true;
                  })
                  .map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                      {opt.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          {selectedOrder?.notes?.trim() && (
            <Box sx={{ mb: 3, p: 2, background: "#ffe6e6ff", borderRadius: 2, overflowWrap: "break-word", wordBreak: "break-word" }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Order Note</Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: Color.primary.main }}>
                {selectedOrder.notes}
              </Typography>
            </Box>
          )}

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            Order Items ({selectedOrder?.items?.length || 0})
          </Typography>

          <Box
            sx={{
              maxHeight: selectedOrder?.items?.length > 2 ? 150 : "none",
              overflowY: selectedOrder?.items?.length > 2 ? "auto" : "visible",
              pr: selectedOrder?.items?.length > 2 ? 1 : 0,
              mb: 2,
            }}
          >
            {selectedOrder?.items?.length > 0 ? (
              selectedOrder.items.map((item) => {
                const isChecked = completedItems[selectedOrder?.id]?.has(item.id) || false;

                return (
                  <Box
                    key={item.id}
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      bgcolor: isChecked ? "#e8f5e9" : "background.paper",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: isChecked ? "success.main" : "#e0e0e0",
                      opacity: isChecked ? 0.9 : 1,
                      transition: "all 0.3s",
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                      <Typography fontSize={14} fontWeight="medium">
                        {item.productName || "Data not found"}
                      </Typography>
                      <Typography fontSize={14} color="text.secondary" sx={{ fontWeight: 600 }}>
                        {item.quantity || "0"} {item.product?.units || "units"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Typography color="text.secondary">No items found.</Typography>
            )}
          </Box>

          {/* Progress Indicator */}
          {selectedOrder && selectedOrder.items.length > 0 && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Completed: {(completedItems[selectedOrder.id]?.size || 0)} / {selectedOrder.items.length}
              </Typography>
              {(completedItems[selectedOrder.id]?.size || 0) === selectedOrder.items.length && (
                <Typography variant="body2" color="success.main" fontWeight="bold">
                  All items completed!
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={statusUpdating ? undefined : cancelStatusUpdate} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Are you sure you want to change the status to{" "}
            <strong style={{ color: Color.secondary.main }}>
              {statusOptions.find((s) => s.value === pendingStatus)?.label || pendingStatus}
            </strong>?
          </Typography>
          {(() => {
            const pendingOrder = orders.find(o => o.id === pendingOrderId) || (selectedOrder?.id === pendingOrderId ? selectedOrder : null);
            const currentStatus = pendingOrder?.cake_status || pendingOrder?.status;
            const isPreparingToReady = pendingStatus === "ready_for_delivery" && currentStatus !== "ready_for_delivery";
            if (isPreparingToReady) {
              return (
                <Stack spacing={2} mt={2} mb={1}>
                  <TextField
                    label="Cake Carton Box Count"
                    type="number"
                    size="small"
                    required
                    fullWidth
                    disabled={statusUpdating}
                    value={cardboardCount}
                    onChange={(e) => setCardboardCount(e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    label="Deliver To"
                    size="small"
                    required
                    fullWidth
                    disabled={statusUpdating}
                    value={deliverTo}
                    onChange={(e) => setDeliverTo(e.target.value)}
                  />
                </Stack>
              );
            }
            return null;
          })()}
        </DialogContent>
        <DialogActions sx={{ mb: 2, mr: 1 }}>
          <Button
            onClick={cancelStatusUpdate}
            variant="outlined"
            disabled={statusUpdating}
            sx={{ border: `1px solid ${Color.secondary.main}`, color: Color.secondary.main }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmStatusUpdate}
            variant="contained"
            disabled={
              statusUpdating ||
              (() => {
                const pendingOrder = orders.find(o => o.id === pendingOrderId) || (selectedOrder?.id === pendingOrderId ? selectedOrder : null);
                const currentStatus = pendingOrder?.cake_status || pendingOrder?.status;
                const isPreparingToReady = pendingStatus === "ready_for_delivery" && currentStatus !== "ready_for_delivery";
                return isPreparingToReady && (!cardboardCount || cardboardCount === "" || !deliverTo || deliverTo.trim() === "");
              })()
            }
            sx={{ background: Color.primary.main }}
            startIcon={statusUpdating ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {statusUpdating ? "Confirming..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}